// Loads legal markdown from public CDN (spinner only), then shows modal when ready.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RevoltModal } from '../../../components';
import {
  parseMarkdown,
  setupCodeBlockCopyButtons,
  setupExternalLinkHandlers,
  renderMath,
} from '../../../core';
import { fetchLegalDocumentMarkdown } from '../legalDocuments';
import './LegalDocumentModal.css';

export function LegalDocumentModal({ document: legalDoc, onClose }) {
  const bodyRef = useRef(null);
  const [markdown, setMarkdown] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const slug = legalDoc?.slug ?? null;
  const fallbackTitle = legalDoc?.displayName ?? 'Legal document';

  useEffect(() => {
    if (!legalDoc || !slug) {
      setLoading(false);
      setMarkdown('');
      setError(null);
      setTitle('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setMarkdown('');
    setTitle(fallbackTitle);

    (async () => {
      try {
        const result = await fetchLegalDocumentMarkdown(slug);
        if (!cancelled) {
          setMarkdown(result.markdown);
          setTitle(result.title || fallbackTitle);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load document');
          setMarkdown('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [legalDoc, slug, fallbackTitle]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || loading || !markdown?.trim()) return;

    el.innerHTML = parseMarkdown(markdown.trim());
    setupCodeBlockCopyButtons(el);
    setupExternalLinkHandlers(el);
    renderMath(el);
  }, [markdown, loading]);

  if (!legalDoc) return null;

  const showSpinner = loading;
  const showModal = !loading && (Boolean(markdown?.trim()) || Boolean(error));

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      {showSpinner &&
        createPortal(
          <div
            className="legal-document-load-overlay"
            onClick={handleOverlayClick}
            role="presentation"
          >
            <div
              className="legal-document-load-spinner"
              role="status"
              aria-label="Loading document"
            />
          </div>,
          document.body
        )}

      {showModal && (
        <RevoltModal
          isOpen
          onClose={onClose}
          title={title || fallbackTitle}
          containerClassName="modal-800 legal-document-modal-container"
        >
          <div className="legal-document-modal-inner">
            {error ? (
              <div className="legal-document-modal-status legal-document-modal-status--error">
                {error}
              </div>
            ) : (
              <div ref={bodyRef} className="legal-document-modal-markdown" />
            )}
          </div>
        </RevoltModal>
      )}
    </>
  );
}
